import React, { useEffect, useState } from 'react';
import './CosmicLoader.css';

/**
 * AstraTerminal Cosmic Loader — v3
 * Minimal constellation of shiny, sparkling stars on pure black.
 *
 * IMPORTANT: The pre-React HTML boot loader (#astra-boot in index.html)
 * shows first. To avoid two loaders visible at once during the boot fade,
 * this React component defers rendering until the HTML boot has been
 * removed from the DOM.
 */
export default function CosmicLoader({ fullscreen = false, label = 'Loading' }) {
  // Wait for the pre-React HTML boot loader to disappear before mounting
  // our own React version to prevent the "two loaders" overlap.
  const [ready, setReady] = useState(() => !document.getElementById('astra-boot'));

  useEffect(() => {
    if (ready) return;
    if (!document.getElementById('astra-boot')) { setReady(true); return; }
    // Watch the body for the boot loader being removed (index.html removes it
    // via el.parentNode.removeChild after fading). MutationObserver fires
    // immediately, avoiding the sub-100ms blank gap of interval polling.
    const obs = new MutationObserver(() => {
      if (!document.getElementById('astra-boot')) {
        setReady(true);
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true, subtree: false });
    // Hard fallback in case boot loader is never removed
    const timeout = setTimeout(() => setReady(true), 4000);
    return () => { obs.disconnect(); clearTimeout(timeout); };
  }, [ready]);

  if (!ready) return null;

  const wrap = fullscreen
    ? 'fixed inset-0 z-[9999] bg-black flex items-center justify-center'
    : 'min-h-[60vh] w-full flex items-center justify-center bg-transparent';

  // Constellation coordinates (viewBox 200x200)
  const stars = [
    { x: 30,  y: 60,  d: 0.0, r: 1.8 },
    { x: 70,  y: 40,  d: 0.4, r: 2.2 },
    { x: 110, y: 80,  d: 0.8, r: 2.6 },
    { x: 100, y: 130, d: 1.2, r: 2.0 },
    { x: 150, y: 110, d: 1.6, r: 2.4 },
    { x: 170, y: 60,  d: 2.0, r: 1.8 },
    { x: 60,  y: 155, d: 2.4, r: 1.6 },
  ];
  const lines = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [3, 6],
  ];

  return (
    <div className={wrap} data-testid="cosmic-loader">
      <div className="cl-wrap">
        <svg
          className="cl-svg"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Constellation lines */}
          {lines.map(([a, b], i) => (
            <line
              key={i}
              x1={stars[a].x} y1={stars[a].y}
              x2={stars[b].x} y2={stars[b].y}
              className="cl-line"
              style={{ animationDelay: `${i * 0.25}s` }}
            />
          ))}
          {/* Stars — halo + core, with staggered twinkle */}
          {stars.map((s, i) => (
            <g key={i} style={{ animationDelay: `${s.d}s` }} className="cl-star-group">
              <circle cx={s.x} cy={s.y} r={s.r * 4} className="cl-star-halo" />
              <circle cx={s.x} cy={s.y} r={s.r * 2} className="cl-star-glow" />
              <circle cx={s.x} cy={s.y} r={s.r} className="cl-star" />
            </g>
          ))}
        </svg>
        {label && <div className="cl-caption" data-testid="cosmic-loader-label">{label}</div>}
      </div>
    </div>
  );
}
